import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { styled } from '@mui/material/styles';
import { usePendingQuery } from '../../services/evidencePay';
import addCommas from '../../utils/functions/commas';
import { ROLES } from '../../utils/constants';
import { checkAccessRights } from '../../utils/functions/accessRight';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import SortableTableLabel from '../../components/Table/SortableTableLabel';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#fff', // หรือ theme.palette.action.hover ถ้าต้องการ
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },

  // [ปรับปรุง] ย้าย :hover มาไว้ที่นี่
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
    position: 'relative',
    zIndex: 10,
    cursor: 'pointer', // เพิ่ม cursor เพื่อให้รู้ว่า hover ได้
  },

  transition: 'transform 0.15s ',
}));
function Pending() {
  const { t } = useTranslation();

  const toNumber = (value) => {
    const parsed = Number(String(value ?? '').replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const columns = [
    { id: 'upload_ref', label: t('app.uploadRef'), minWidth: 100 },

    {
      id: 'invoice_no',
      label: t('app.invoiceNo'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'customer_name',
      label: t('app.customerName'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'finance_name',
      label: t('app.financeOfficer'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'total_pay_amount',
      label: t('app.grandTotal'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'transfered_date',
      label: t('app.paymentDate'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'createdAt',
      label: t('app.importDate'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'payer_email',
      label: t('app.uploadBy'),
      minWidth: 120,
      align: 'center',
    },
    {
      id: 'sale_name',
      label: t('app.verify'),
      minWidth: 100,
      align: 'center',
      sortable: false,
    },
  ];

  const [rowsPerPage, setRowsPerPage] = useState(5); // จำนวนแถวต่อหน้า
  const [page, setPage] = useState(0); // หน้าปัจจุบัน (เริ่มจากหน้า 0)
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [total, setTotal] = useState(10);

  const { value: merchant, allMerchant } = useSelector(
    (state) => state.merchant,
  );
  const [openDrawer, setOpenDrawer] = useState(false);
  const [dataRowDetail, setDataRowDetail] = useState();
  const [uploadRef, setUploadRef] = useState('');

  const [searchNumber, setSearchNumber] = useState('');
  const [searchAll, setSearchAll] = useState('');
  const [openDel, setOpenDel] = useState(false);
  const navigate = useNavigate();

  const isAdminRole = checkAccessRights(ROLES.ADMIN_ROLE);
  const sorgcode = isAdminRole ? merchant || '' : allMerchant || '';
  const shouldSkip = isAdminRole && !merchant;
  const {
    data: evidencePay,
    isLoading,
    isFetching,
    refetch,
  } = usePendingQuery(
    {
      sorgcode,
      page: page === 0 ? 1 : page + 1,
      size: rowsPerPage,
      status: 'Upload',
      search: searchAll,
      sortBy,
      sortOrder,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    },
  );
  // useEffect(() => {
  //   if (evidencePay?.data) {
  //     const refs = evidencePay.data.map((row) => row.upload_ref);
  //     setUploadRef(refs);
  //   } else {
  //     setUploadRef([]);
  //   }
  // }, [evidencePay]);

  const handleNavigation = (item) => {
    navigate('/pending-detail', {
      state: { _id: item._id, uploadRef: item.upload_ref },
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0); // เริ่มที่หน้า 0 เมื่อเปลี่ยนจำนวนแถวต่อหน้า
  };

  const handleRequestSort = (property) => {
    setSortOrder(
      sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc',
    );
    setSortBy(property);
    setPage(0);
  };

  useEffect(() => {
    if (evidencePay) {
      const { pagination } = evidencePay;

      setTotal(pagination.total);
    }
  }, [evidencePay, searchAll]);

  useEffect(() => {
    setPage(0);
  }, [searchAll]);

  const handleClose = () => {
    setOpenDel(false);
  };

  return (
    <div>
    
      <div className="flex flex-col">
        {/* <Loading isLoading={isLoading} /> */}

        <div className="font-bold">{t('app.pendingStatus')}</div>
        <div className="w-full lg:w-[20%]">
          <Paper
            component="form"
            sx={{
              p: '2px 4px',
              display: 'flex',
              alignItems: 'center',
              marginTop: 2,
            }}
          >
            <InputBase
              className='"app-search-input'
              type="text"
              sx={{ ml: 1, flex: 1, width: 300 }}
              onChange={(e) => {
                setSearchAll(e.target.value);
              }}
              placeholder={t('app.search')}
            />

            <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
            {/* <IconButton color="primary" sx={{ p: '10px' }} onClick={() => {}}> */}
            <div className="p-2">
              <SearchIcon sx={{ fontSize: '24px' }} color="primary" />
            </div>
          </Paper>
        </div>

        <div className="flex flex-col mt-5">
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <StyledTableCell
                        key={column.id}
                        align={column.align}
                        style={{ minWidth: column.minWidth }}
                      >
                        <div style={{ whiteSpace: 'nowrap', padding: 5 }}>
                          <SortableTableLabel
                            column={column}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleRequestSort}
                            color="white"
                          />
                        </div>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {isFetching ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : _.size(evidencePay?.data) ? (
                  <TableBody>
                    {evidencePay?.data?.map((item, index) => (
                        <StyledTableRow
                          hover
                          role="checkbox"
                          style={{ whiteSpace: 'nowrap' }}
                          tabIndex={-1}
                          key={index}
                        >
                          {columns.map((column) => {
                            const value = item[column.id];

                            // เฉพาะ Invoice No. ให้ wrap ได้
                            const cellStyle =
                              column.id === 'invoice_no'
                                ? {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-all',
                                  }
                                : { whiteSpace: 'nowrap' };

                            if (column.id === 'upload_ref') {
                              return (
                                <TableCell
                                  sx={{
                                    color: 'primary.light',
                                    fontWeight: 'bold',
                                    fontFamily: 'monospace',
                                    ...cellStyle,
                                  }}
                                  key={column.id}
                                  align={column.align}
                                >
                                  {value}
                                </TableCell>
                              );
                            }
                            if (column.id === 'invoice_no') {
                              const invoiceList = (
                                Array.isArray(value)
                                  ? value
                                  : typeof value === 'string'
                                    ? value.split(',').map((v) => v.trim())
                                    : []
                              )
                                .filter(Boolean)
                                .sort();

                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  ...cellStyle,
                                  verticalAlign: 'top', // ให้ตัวเลขเริ่มแสดงจากด้านบนของเซลล์
                                  paddingY: 1.5,
                                }}
                              >
                                <div className="flex flex-col gap-1">
                                  {invoiceList.length > 0 ? (
                                    invoiceList.map((inv, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          
                                          fontFamily: 'monospace',
                                          fontSize: '0.875rem',
                                          fontWeight: 600,
                                          color: '#333',
                                          backgroundColor: '#f5f5f5', // ใส่พื้นหลังอ่อนๆ ให้ดูแยกเป็นเส้นๆ
                                          padding: '2px 8px',
                                          borderRadius: '4px',
                                          width: 'fit-content', // ให้กว้างเท่าตัวอักษร
                                          display: 'inline-block',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {inv}
                                      </div>
                                    ))
                                  ) : (
                                    <span>-</span>
                                  )}
                                </div>
                              </TableCell>
                            );
                          }
                          if (column.id === 'total_pay_amount') {
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={cellStyle}
                              >
                                {addCommas(toNumber(item.total_pay_amount))}
                              </TableCell>
                            );
                          }

                            if (column.id === 'transfered_date') {
                              return (
                                <TableCell
                                  key={column.id}
                                  align={column.align}
                                  sx={cellStyle}
                                >
                                  {dayjs(value).format('YYYY-MM-DD • HH:mm:ss')}
                                </TableCell>
                              );
                            }
                            if (column.id === 'sale_name') {
                              return (
                                <TableCell
                                  key={column.id}
                                  align={column.align}
                                  sx={cellStyle}
                                >
                                  <Button
                                    onClick={() => {
                                      handleNavigation(item);
                                    }}
                                    size="small"
                                    variant="contained"
                                    sx={{ color: 'white' }}
                                  >
                                    <FileDownloadDoneIcon />
                                    {t('app.check')}
                                  </Button>
                                </TableCell>
                              );
                            }
                            if (column.id === 'createdAt') {
                              return (
                                <TableCell
                                  key={column.id}
                                  align={column.align}
                                  sx={cellStyle}
                                >
                                  {dayjs(value).format('YYYY-MM-DD • HH:mm:ss')}
                                </TableCell>
                              );
                            }
                            if (column.id === 'invoice_no') {
                              const invoiceList = Array.isArray(value)
                                ? value
                                : typeof value === 'string'
                                  ? value.split(',').map((v) => v.trim())
                                  : [];

                              return (
                                <TableCell
                                  key={column.id}
                                  align={column.align}
                                  sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {invoiceList.length > 1 ? (
                                    <ul
                                      style={{ paddingLeft: '1em', margin: 0 }}
                                    >
                                      {invoiceList.map((inv, index) => (
                                        <li
                                          key={index}
                                          style={{ listStyleType: 'disc' }}
                                        >
                                          {inv}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    invoiceList[0] || '-' // แสดงแค่ตัวเดียว ไม่มีจุด
                                  )}
                                </TableCell>
                              );
                            }

                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={cellStyle}
                              >
                                {value}
                              </TableCell>
                            );
                          })}
                        </StyledTableRow>
                      ))}
                  </TableBody>
                ) : (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <div>ไม่มีข้อมูล</div>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 25, 100]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </div>
      </div>
    </div>
  );
}

export default Pending;
